(() => {
  'use strict';
  const normal = (value) => String(value || '').trim().toLowerCase();
  function modelKey(product) {
    const vendor = normal(product.vendor);
    if (normal(product.model)) return vendor + '|custom|' + normal(product.model);
    const sku = String(product.sku || '').trim().toUpperCase();
    const patterns = {
      'fly london': /^(?:FLY)?(P\d{6})\d{3}(?:\|\d{2}(?:\.5)?)?$/,
      'softinos': /^(?:SOF)?(P\d{6})\d{3}(?:\|\d{2}(?:\.5)?)?$/,
      'as portuguesas': /^(?:ASP)?(P\d{6})\d{3}(?:\|\d{2}(?:\.5)?)?$/,
      'fred & frederico': /^(?:F&F)?(P\d{6}|\d{3}[A-Z]+)\d{3}(?:SC|S\/C)?(?:\|\d{2})?$/,
      'meias solas': /^(MS-(?:CIN-PEL|BAM-PA|BAM-PM|ALG-[A-Z]+))-[A-Z]{3}-\d+$/
    };
    const match = patterns[vendor] && sku.match(patterns[vendor]);
    return match ? vendor + '|sku|' + match[1] : null;
  }
  function colourName(product) {
    if (String(product.colour || '').trim()) return String(product.colour).trim();
    // Current footwear titles use BRAND MODEL [number] [rug] [cor] COLOUR.
    const match = String(product.title || '').match(/\b(?:FLY\s+London|Softinos|As\s+Portuguesas|Fred\s*&\s*Frederico)\s+\S+(?:\s+(?:\d+(?:\.\d+)?|[LM]))?\s+(?:rug\s+)?(?:cor\s+)?(.+)$/i);
    if (match) return match[1].trim().replace(/^./u, (char) => char.toUpperCase());
    if (normal(product.vendor) === 'meias solas') {
      const code = String(product.sku || '').split('-').at(-2);
      const names = {CAM:'Camel',CAS:'Castanho',CAE:'Castanho escuro',AZE:'Azul escuro',PRE:'Preto',AZU:'Azul',CIN:'Cinza claro',ANT:'Antracite'};
      if (modelKey(product) && names[code]) return names[code];
    }
    // Never guess a colour from a numeric supplier code.
    return product.title || 'Ver artigo';
  }
  function siblings(products, current, showSoldout) {
    const key = modelKey(current);
    if (!key) return [];
    const seen = new Set();
    return [current, ...products].filter((p) => {
      if (modelKey(p) !== key || seen.has(String(p.id))) return false;
      seen.add(String(p.id));
      return showSoldout || p.available || String(p.id) === String(current.id);
    }).sort((a, b) => {
      if (String(a.id) === String(current.id)) return -1;
      if (String(b.id) === String(current.id)) return 1;
      return colourName(a).localeCompare(colourName(b), 'pt');
    });
  }
  if (typeof module !== 'undefined' && module.exports) module.exports = { modelKey, colourName, siblings };
  if (typeof window === 'undefined' || !window.customElements || customElements.get('ms-product-colours')) return;
  const requests = new Map();
  async function catalogue(rawURL) {
    const base = new URL(rawURL, window.location.origin);
    if (base.origin !== window.location.origin) throw new Error('Invalid catalogue origin');
    base.searchParams.set('view', 'ms-colour-index');
    // Carry the unpublished theme context across requests when present.
    const preview = new URL(window.location.href).searchParams.get('preview_theme_id');
    if (preview) base.searchParams.set('preview_theme_id', preview);
    const key = base.href;
    if (!requests.has(key)) {
      const pending = (async () => {
        const products = [];
        const seen = new Set();
        let pages = 1;
        for (let page = 1; page <= pages; page++) {
          const url = new URL(base);
          url.searchParams.set('page', String(page));
          const controller = new AbortController();
          const timer = setTimeout(() => controller.abort(), 15000);
          let data;
          try {
            const response = await fetch(url.href, { credentials: 'same-origin', signal: controller.signal });
            if (!response.ok) throw new Error('Catalogue HTTP ' + response.status);
            data = JSON.parse(await response.text());
          } finally { clearTimeout(timer); }
          if (data.version !== 1 || data.page !== page || !Number.isInteger(data.pages) || data.pages > 100 || !Array.isArray(data.products)) throw new Error('Invalid colour catalogue');
          if (page > 1 && pages !== data.pages) throw new Error('Catalogue changed during pagination');
          pages = data.pages;
          for (const product of data.products) {
            if (!product.id || seen.has(String(product.id))) throw new Error('Repeated colour catalogue page');
            seen.add(String(product.id));
            products.push(product);
          }
        }
        return products;
      })();
      requests.set(key, pending);
      pending.catch(() => requests.delete(key));
    }
    return requests.get(key);
  }
  class ProductColours extends HTMLElement {
    connectedCallback() {
      if (this.started) return;
      this.started = true;
      this.load();
    }
    async load() {
      try {
        const current = JSON.parse(this.querySelector('[data-current-product]').textContent);
        if (!modelKey(current)) return;
        const products = await catalogue(this.dataset.catalogueUrl);
        if (!this.isConnected) return;
        const colours = siblings(products, current, this.dataset.showSoldout === 'true');
        if (colours.length < 2) return;
        const list = this.querySelector('ul');
        const fragment = document.createDocumentFragment();
        for (const product of colours) {
          const url = new URL(product.url, window.location.origin);
          if (url.origin !== window.location.origin) continue;
          const preview = new URL(window.location.href).searchParams.get('preview_theme_id');
          if (preview) url.searchParams.set('preview_theme_id', preview);
          const name = colourName(product);
          const li = document.createElement('li');
          li.className = 'ms-product-colours__item' + (product.available ? '' : ' ms-product-colours__item--soldout');
          const link = document.createElement('a');
          link.className = 'ms-product-colours__link';
          link.href = url.href;
          link.title = name + (product.available ? '' : ' — Esgotado');
          link.setAttribute('aria-label', link.title);
          if (String(product.id) === String(current.id)) link.setAttribute('aria-current', 'page');
          if (product.image) {
            const image = document.createElement('img');
            image.className = 'ms-product-colours__image';
            image.src = product.image;
            image.alt = '';
            image.width = 120;
            image.height = 120;
            image.loading = 'lazy';
            link.append(image);
          }
          const label = document.createElement('span');
          label.className = 'ms-product-colours__name';
          label.textContent = name.replace(/\s*\/\s*/g, ' ');
          link.append(label);
          if (!product.available) {
            const status = document.createElement('span');
            status.className = 'ms-product-colours__status';
            status.textContent = 'Esgotado';
            link.append(status);
          }
          li.append(link);
          fragment.append(li);
        }
        list.replaceChildren(fragment);
        this.hidden = list.children.length < 2;
      } catch (error) {
        // The normal size selector and purchase form remain usable on failure.
        console.warn('[Meias Solas] Não foi possível carregar as cores:', error.message);
      }
    }
  }
  customElements.define('ms-product-colours', ProductColours);
})();
